import React, { useState, useEffect } from 'react';
import { lineupService } from '../../../shared/services/lineupService';
import { MatchLineup } from '../../../shared/types/database';

interface PlayerSelectionProps {
  matchId?: string;
  leftTeamId?: string;
  rightTeamId?: string;
  leftTeamName?: string;
  rightTeamName?: string;
  selectedPlayerId?: string | null;
  onPlayerSelect?: (playerId: string | null) => void;
}

const PlayerSelection: React.FC<PlayerSelectionProps> = ({ 
  matchId, 
  leftTeamId, 
  rightTeamId,
  leftTeamName,
  rightTeamName,
  selectedPlayerId,
  onPlayerSelect
}) => {
  const [activeTeam, setActiveTeam] = useState<'left' | 'right'>('left');
  const [leftTeamPlayers, setLeftTeamPlayers] = useState<MatchLineup[]>([]);
  const [rightTeamPlayers, setRightTeamPlayers] = useState<MatchLineup[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchLineups = async () => {
      if (!matchId || !leftTeamId || !rightTeamId) return;

      setLoading(true);
      try {
        const [leftLineup, rightLineup] = await Promise.all([
          lineupService.getMatchLineup(matchId, leftTeamId),
          lineupService.getMatchLineup(matchId, rightTeamId)
        ]);
        
        setLeftTeamPlayers(leftLineup || []);
        setRightTeamPlayers(rightLineup || []);
      } catch (error) {
        console.error('Error fetching lineups:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchLineups();
  }, [matchId, leftTeamId, rightTeamId]);

  const allPlayers = activeTeam === 'left' ? leftTeamPlayers : rightTeamPlayers;
  // Only show players actually in the lineup (exclude empty slots)
  const currentPlayers = allPlayers.filter((p) => p.player_id && p.player);

  if (!matchId) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <p className="text-sm text-gray-500 text-center">Select a match to view players</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center justify-center py-8">
          <div className="flex items-center space-x-2 text-gray-500">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
            <span className="text-sm">Loading players...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200">
      {/* Team Tabs */}
      <div className="flex bg-gray-100 rounded-t-lg p-1">
        <button
          onClick={() => setActiveTeam('left')}
          className={`flex-1 px-3 py-1 text-xs rounded-md transition-colors font-medium ${
            activeTeam === 'left'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          {leftTeamName || 'Team A'}
        </button>
        <button
          onClick={() => setActiveTeam('right')}
          className={`flex-1 px-3 py-1 text-xs rounded-md transition-colors font-medium ${
            activeTeam === 'right'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          {rightTeamName || 'Team B'}
        </button>
      </div>

      {/* Players Grid */}
      <div className="p-3">
        {currentPlayers.length === 0 ? (
          <div className="text-center py-6">
            <svg className="w-8 h-8 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
            <p className="text-[10px] text-gray-500 mt-1">No players in lineup</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-1.5">
            {currentPlayers
              .sort((a, b) => (a.kit_number || 0) - (b.kit_number || 0))
              .map((player) => (
                <div
                  key={player.id}
                  onClick={() => {
                    const isSelected = selectedPlayerId === player.player_id;
                    onPlayerSelect?.(isSelected ? null : player.player_id);
                  }}
                  className={`flex items-center space-x-1.5 p-1.5 rounded border transition-colors cursor-pointer ${
                    selectedPlayerId === player.player_id
                      ? 'bg-blue-100 border-blue-400 ring-2 ring-blue-300'
                      : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                  }`}
                >
                  {/* Player thumbnail (identification screenshot or profile image) */}
                  {(player.identification_screenshot_url || player.player?.profile_image) ? (
                    <div className="flex-shrink-0 w-8 h-8 rounded overflow-hidden bg-gray-200 border border-gray-300">
                      <img
                        src={player.identification_screenshot_url || player.player?.profile_image || ''}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : (
                    <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-bold ${
                      selectedPlayerId === player.player_id 
                        ? 'bg-blue-700 text-white' 
                        : 'bg-blue-600 text-white'
                    }`}>
                      {player.kit_number || player.player?.kit_number || '?'}
                    </div>
                  )}
                  
                  {/* Player Info */}
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] font-medium text-gray-900 truncate leading-tight">
                      {player.player?.full_name || 'Unknown Player'}
                    </div>
                    <div className="text-[9px] text-gray-500 truncate leading-tight">
                      {player.position || player.player?.position || 'N/A'}
                    </div>
                  </div>
                  {/* Kit number badge when thumbnail is shown */}
                  {(player.identification_screenshot_url || player.player?.profile_image) && (
                    <div className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold ${
                      selectedPlayerId === player.player_id ? 'bg-blue-700 text-white' : 'bg-blue-600 text-white'
                    }`}>
                      {player.kit_number || player.player?.kit_number || '?'}
                    </div>
                  )}
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PlayerSelection;

